import React, { useState, useEffect } from 'react';
import { Activity, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { IMAGING_TYPES, BODY_PARTS, MEDICAL_SPECIALTIES } from '../../constants/mediscribe';
import { OrderChipRow } from './OrderChipRow';

/* ─────────────────────────────────────────────────────────────────
   Helpers preserved from the previous bulky-row implementation —
   they handle the "Other: <freetext>" convention and the legacy
   `Specialty || indication` round-trip serialization so existing
   data continues to work without migration.
   ───────────────────────────────────────────────────────────────── */
const isOther = (val) => typeof val === 'string' && (val === 'Other' || val.startsWith('Other:'));
const otherDescOf = (val) => {
  if (typeof val !== 'string') return '';
  if (val === 'Other') return '';
  if (val.startsWith('Other:')) return val.slice('Other:'.length).trimStart();
  return '';
};
const composeOther = (desc) => {
  const trimmed = (desc || '').trim();
  return trimmed ? `Other: ${trimmed}` : 'Other';
};

const splitSpec = (val) => {
  if (typeof val !== 'string') return { specialty: '', indication: '' };
  const idx = val.indexOf('||');
  if (idx === -1) return { specialty: val, indication: '' };
  return {
    specialty: val.slice(0, idx).trim(),
    indication: val.slice(idx + 2).trim(),
  };
};
const joinSpec = (specialty, indication) => {
  const ind = (indication || '').trim();
  if (!ind) return specialty || '';
  return `${specialty || ''} || ${ind}`;
};

/* ─────────────────────────────────────────────────────────────────
   Quick-pick lists. These are the top N most-common values that get
   surfaced as 1-tap pills inside the popover — the full IMAGING_TYPES
   / MEDICAL_SPECIALTIES dropdowns are still available behind a
   "More…" Select fallback for rare types.
   ───────────────────────────────────────────────────────────────── */
const IMAGING_QUICK_TYPES = [
  'X-ray',
  'Ultrasound',
  'CT w/o contrast',
  'CT w/ contrast',
  'CT w/ & w/o contrast',
  'MRI w/o contrast',
  'MRI w/ contrast',
  'MRI w/ & w/o contrast',
];
const IMAGING_QUICK_BODY = ['Head', 'C-Spine', 'L-Spine', 'Shoulder', 'Knee', 'Abdomen'];
const REFERRAL_QUICK = ['Ortho', 'Cardio', 'GI', 'Neuro', 'Derm', 'GYN', 'PT', 'BH'];
// Map quick-pick short names to the canonical MEDICAL_SPECIALTIES value
// when they differ (e.g. "Cardio" pill → "Cardiology").
// Mar 3, 2026 — Ortho normalized to "Orthopedic Surgery" (was "Orthopedics")
// so it matches AUTO_SEED_PATTERNS (App.js) and the extractor pattern
// (useMediscribe.js). Previously the two paths added *different* chip
// values ("Orthopedic Surgery" from auto-seed vs "Orthopedics" from
// manual pill) and dedupe couldn't collapse them into one chip.
const REFERRAL_ALIAS = {
  Ortho: 'Orthopedic Surgery',
  Cardio: 'Cardiology',
  GI: 'Gastroenterology',
  Neuro: 'Neurology',
  Derm: 'Dermatology',
  GYN: 'GYN',
  PT: 'Physical Therapy',
  BH: 'Behavioral Health',
};

/* ─────────────────────────────────────────────────────────────────
   CharCounter — live amber/red warning before the Genesis hard cap.
   Genesis caps imaging indications at 400 chars and referral
   indications at 250 — exceeding silently truncates server-side.
   ───────────────────────────────────────────────────────────────── */
const CharCounter = ({ count, amber, red, hardCap, testId }) => {
  let cls = 'text-muted-foreground';
  if (count >= red) cls = 'text-red-500 font-semibold';
  else if (count >= amber) cls = 'text-amber-500 font-semibold';
  return (
    <span className={`text-[9px] tabular-nums ${cls}`} data-testid={testId}>
      {count}/{hardCap}
    </span>
  );
};

const QuickPills = ({ options, value, onPick, accent = 'primary', testIdPrefix }) => (
  <div className="flex flex-wrap gap-1 mb-2">
    {options.map((opt) => {
      const on = value === opt;
      return (
        <button
          key={opt}
          type="button"
          onClick={() => onPick(opt)}
          data-testid={`${testIdPrefix}-${opt.toLowerCase().replace(/\s+/g, '-')}`}
          className={`px-2.5 py-1 text-[11px] font-semibold transition-colors ${
            on ? `bg-${accent}/15 border-${accent}/50 text-${accent}` : 'bg-background border-border text-foreground hover:border-border-hot'
          }`}
          style={{
            borderRadius: '3px',
            borderWidth: '1px',
            borderStyle: 'solid',
            ...(on ? {
              background: 'rgba(108,182,255,0.14)',
              borderColor: 'rgba(108,182,255,0.55)',
              color: '#6cb6ff',
            } : {}),
          }}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

/* ═════════════════════════════════════════════════════════════════
   ImagingOrdersInput
   ═════════════════════════════════════════════════════════════════ */

const ImagingEditor = ({ value, onSave, onCancel }) => {
  const [type, setType] = useState(value?.type || '');
  const [bodyPart, setBodyPart] = useState(value?.bodyPart || '');
  const [indication, setIndication] = useState(value?.indication || '');
  const [typeOther, setTypeOther] = useState(isOther(value?.type) ? otherDescOf(value?.type) : '');
  const [bodyOther, setBodyOther] = useState(isOther(value?.bodyPart) ? otherDescOf(value?.bodyPart) : '');

  const IMG_AMBER = 360, IMG_RED = 390, IMG_CAP = 400;
  const indLen = indication.length;

  const pickType = (t) => {
    if (t === 'Other') setType('Other');
    else { setType(t); setTypeOther(''); }
  };
  const pickBody = (b) => {
    if (b === 'Other') setBodyPart('Other');
    else { setBodyPart(b); setBodyOther(''); }
  };

  const handleSave = () => {
    if (!type) return;
    const resolvedType = isOther(type) ? composeOther(typeOther) : type;
    const resolvedBody = bodyPart ? (isOther(bodyPart) ? composeOther(bodyOther) : bodyPart) : '';
    onSave({ type: resolvedType, bodyPart: resolvedBody, indication: indication.trim() });
  };

  return (
    <div className="space-y-2" data-testid="imaging-editor">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Type</div>
        <QuickPills
          options={IMAGING_QUICK_TYPES}
          value={isOther(type) ? null : type}
          onPick={pickType}
          accent="primary"
          testIdPrefix="imaging-pill-type"
        />
        <Select value={isOther(type) ? 'Other' : (IMAGING_QUICK_TYPES.includes(type) ? '' : type)} onValueChange={pickType}>
          <SelectTrigger className="h-8 text-xs" data-testid="imaging-select-type">
            <SelectValue placeholder="More types…" />
          </SelectTrigger>
          <SelectContent>
            {IMAGING_TYPES.filter((t) => !IMAGING_QUICK_TYPES.includes(t)).map((t) => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isOther(type) && (
          <Input
            value={typeOther}
            onChange={(e) => setTypeOther(e.target.value)}
            placeholder='Describe imaging study (e.g., "Stress echo")'
            className="h-7 text-[11px] mt-1.5 bg-input/60 border-amber-500/40"
            autoFocus
            data-testid="imaging-other-type"
          />
        )}
      </div>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Body part / location</div>
        <QuickPills
          options={IMAGING_QUICK_BODY}
          value={isOther(bodyPart) ? null : bodyPart}
          onPick={pickBody}
          accent="primary"
          testIdPrefix="imaging-pill-body"
        />
        <Select value={isOther(bodyPart) ? 'Other' : (IMAGING_QUICK_BODY.includes(bodyPart) ? '' : bodyPart)} onValueChange={pickBody}>
          <SelectTrigger className="h-8 text-xs" data-testid="imaging-select-body">
            <SelectValue placeholder="More body parts…" />
          </SelectTrigger>
          <SelectContent>
            {BODY_PARTS.filter((b) => !IMAGING_QUICK_BODY.includes(b)).map((b) => (
              <SelectItem key={b} value={b}>{b}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {isOther(bodyPart) && (
          <Input
            value={bodyOther}
            onChange={(e) => setBodyOther(e.target.value)}
            placeholder='Describe location (e.g., "Plantar fascia")'
            className="h-7 text-[11px] mt-1.5 bg-input/60 border-amber-500/40"
            data-testid="imaging-other-body"
          />
        )}
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Indication <span className="text-muted-foreground/60 font-normal normal-case">— optional</span></span>
          <CharCounter count={indLen} amber={IMG_AMBER} red={IMG_RED} hardCap={IMG_CAP} testId="imaging-ind-count" />
        </div>
        <Input
          value={indication}
          onChange={(e) => setIndication(e.target.value)}
          onBlur={(e) => {
            // Mar 2, 2026 — Only auto-commit when blur is leaving the whole
            // editor. If focus is moving to the Cancel button (or any button
            // inside the popover) we must NOT save — that steals the Cancel
            // click and the popover closes as if Save were pressed.
            const next = e.relatedTarget;
            if (next && next.closest && next.closest('[data-imaging-editor-actions="1"]')) return;
            if (type) handleSave();
          }}
          placeholder="Focus this study (e.g., chronic R knee pain, eval meniscus)"
          className={`h-8 text-xs ${indLen >= IMG_RED ? 'border-red-500/60' : indLen >= IMG_AMBER ? 'border-amber-500/60' : ''}`}
          maxLength={IMG_CAP + 50}
          data-testid="imaging-indication"
        />
      </div>
      <div className="flex justify-end gap-1.5 pt-1" data-imaging-editor-actions="1">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-[11px] font-semibold text-muted-foreground border border-border hover:border-border-hot"
          style={{ borderRadius: '3px', background: 'transparent' }}
          data-testid="imaging-cancel"
        >Cancel</button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!type || (isOther(type) && !typeOther.trim())}
          className="px-3 py-1.5 text-[11px] font-bold disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ borderRadius: '3px', background: '#6cb6ff', color: '#0f1419', border: '1px solid #6cb6ff' }}
          data-testid="imaging-save"
        >Save</button>
      </div>
    </div>
  );
};

const renderImagingChip = (img) => {
  const typeLabel = isOther(img.type) ? (otherDescOf(img.type) || 'Other') : (img.type || '?');
  const body = isOther(img.bodyPart) ? otherDescOf(img.bodyPart) : (img.bodyPart || '');
  return { label: typeLabel, detail: body };
};

export const ImagingOrdersInput = ({
  imagingOrders,
  addImaging,        // (entry?) optional payload from chip editor
  removeImaging,
  updateImagingType,
  updateImagingBodyPart,
  updateImagingIndication,
  restoreImaging,
  quickActions,
  addQuickActionImaging,
  // Feb 28, 2026 (v6.8.43) — auto-seed explainer wiring
  autoSeedReasons,
  clearAutoSeedReason,
}) => {
  // Filter out "blank" placeholder rows from the legacy add-then-fill
  // flow so the chip-row only shows actually-saved entries.
  const items = (imagingOrders || []).filter((i) => i && (i.type || i.bodyPart || i.indication));

  const handleAdd = (entry) => {
    // Look for a blank row to fill, otherwise call addImaging then update.
    const blankIdx = (imagingOrders || []).findIndex((i) => !i || (!i.type && !i.bodyPart && !i.indication));
    if (blankIdx >= 0) {
      updateImagingType(blankIdx, entry.type);
      updateImagingBodyPart(blankIdx, entry.bodyPart || '');
      if (typeof updateImagingIndication === 'function') updateImagingIndication(blankIdx, entry.indication || '');
    } else {
      addImaging();
      // setTimeout so the new row is mounted before we patch it.
      setTimeout(() => {
        const newIdx = (imagingOrders || []).length;
        updateImagingType(newIdx, entry.type);
        updateImagingBodyPart(newIdx, entry.bodyPart || '');
        if (typeof updateImagingIndication === 'function') updateImagingIndication(newIdx, entry.indication || '');
      }, 0);
    }
  };

  const handleUpdate = (index, entry) => {
    // index here is the index in our filtered `items` array — translate
    // back to the original imagingOrders index.
    const realIdx = imagingOrders.findIndex((i) => i === items[index]);
    if (realIdx < 0) return;
    updateImagingType(realIdx, entry.type);
    updateImagingBodyPart(realIdx, entry.bodyPart || '');
    if (typeof updateImagingIndication === 'function') updateImagingIndication(realIdx, entry.indication || '');
  };

  const handleRemove = (index) => {
    const removedItem = items[index];
    const realIdx = imagingOrders.findIndex((i) => i === removedItem);
    if (realIdx < 0 || !removedItem) return;
    removeImaging(realIdx);
    // Mar 3, 2026 — Undo toast (same pattern as CC-pivot auto-clear).
    // Uses the atomic `restoreImaging` helper to avoid stale-closure races
    // between the addImaging + updateImaging setState calls.
    const chipLabel = removedItem.type
      ? `${removedItem.type}${removedItem.bodyPart ? ' · ' + removedItem.bodyPart : ''}`
      : 'imaging order';
    toast(`Removed ${chipLabel}`, {
      duration: 6000,
      action: {
        label: 'Undo',
        onClick: () => {
          if (typeof restoreImaging === 'function') {
            restoreImaging(removedItem);
          }
        },
      },
    });
  };

  const isStarred = (img) =>
    quickActions?.imaging?.some((qa) => qa.type === img.type && qa.bodyPart === (img.bodyPart || ''));

  // v6.8.43 — auto-seed "why?" lookups. Key format: `imaging:<type>`.
  const getChipReason = (img) => (autoSeedReasons || {})[`imaging:${img?.type}`] || null;
  const handleClearChipReason = (img) => {
    if (clearAutoSeedReason && img?.type) clearAutoSeedReason(`imaging:${img.type}`);
  };

  return (
    <OrderChipRow
      items={items}
      renderChip={renderImagingChip}
      renderEditor={({ value, onSave, onCancel }) => (
        <ImagingEditor value={value} onSave={onSave} onCancel={onCancel} />
      )}
      addLabel="Add imaging"
      accent="primary"
      sectionTitle="Imaging Orders"
      sectionIcon={<Activity className="w-3.5 h-3.5 text-primary" />}
      testIdPrefix="imaging-chip"
      onAdd={handleAdd}
      onUpdate={handleUpdate}
      onRemove={handleRemove}
      onFavorite={(img) => addQuickActionImaging(img.type, img.bodyPart)}
      isStarred={isStarred}
      getChipReason={getChipReason}
      onClearChipReason={handleClearChipReason}
    />
  );
};

/* ═════════════════════════════════════════════════════════════════
   SpecialtyReferralsInput
   ═════════════════════════════════════════════════════════════════ */

const ReferralEditor = ({ value, onSave, onCancel }) => {
  const parsed = value ? splitSpec(value) : { specialty: '', indication: '' };
  const [spec, setSpec] = useState(parsed.specialty);
  const [indication, setIndication] = useState(parsed.indication);
  const [specOther, setSpecOther] = useState(isOther(parsed.specialty) ? otherDescOf(parsed.specialty) : '');

  const REF_AMBER = 220, REF_RED = 245, REF_CAP = 250;
  const indLen = indication.length;

  const pickSpec = (s) => {
    if (s === 'Other') setSpec('Other');
    else { setSpec(s); setSpecOther(''); }
  };

  const handleSave = () => {
    if (!spec) return;
    const resolved = isOther(spec) ? composeOther(specOther) : spec;
    onSave(joinSpec(resolved, indication.trim()));
  };

  return (
    <div className="space-y-2" data-testid="referral-editor">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">Specialty</div>
        <QuickPills
          options={REFERRAL_QUICK}
          value={REFERRAL_QUICK.find((q) => REFERRAL_ALIAS[q] === spec || q === spec)}
          onPick={(q) => pickSpec(REFERRAL_ALIAS[q] || q)}
          accent="violet"
          testIdPrefix="referral-pill-spec"
        />
        <Select
          value={isOther(spec) ? 'Other' : (REFERRAL_QUICK.some((q) => (REFERRAL_ALIAS[q] || q) === spec) ? '' : spec)}
          onValueChange={pickSpec}
        >
          <SelectTrigger className="h-8 text-xs" data-testid="referral-select-spec">
            <SelectValue placeholder="More specialties…" />
          </SelectTrigger>
          <SelectContent>
            {MEDICAL_SPECIALTIES
              .filter((s) => !REFERRAL_QUICK.some((q) => (REFERRAL_ALIAS[q] || q) === s))
              .map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        {isOther(spec) && (
          <Input
            value={specOther}
            onChange={(e) => setSpecOther(e.target.value)}
            placeholder='Describe specialty (e.g., "Wound care clinic")'
            className="h-7 text-[11px] mt-1.5 bg-input/60 border-amber-500/40"
            autoFocus
            data-testid="referral-other-spec"
          />
        )}
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Indication <span className="text-muted-foreground/60 font-normal normal-case">— optional</span></span>
          <CharCounter count={indLen} amber={REF_AMBER} red={REF_RED} hardCap={REF_CAP} testId="referral-ind-count" />
        </div>
        <Input
          value={indication}
          onChange={(e) => setIndication(e.target.value)}
          onBlur={(e) => {
            // See ImagingEditor blur handler — same guard so the Cancel
            // button on referrals actually cancels instead of the blur
            // committing first.
            const next = e.relatedTarget;
            if (next && next.closest && next.closest('[data-referral-editor-actions="1"]')) return;
            if (spec) handleSave();
          }}
          placeholder="Focus this referral (e.g., R shoulder pain × 6w, +Hawkins)"
          className={`h-8 text-xs ${indLen >= REF_RED ? 'border-red-500/60' : indLen >= REF_AMBER ? 'border-amber-500/60' : ''}`}
          maxLength={REF_CAP + 50}
          data-testid="referral-indication"
        />
      </div>
      <div className="flex justify-end gap-1.5 pt-1" data-referral-editor-actions="1">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-[11px] font-semibold text-muted-foreground border border-border hover:border-border-hot"
          style={{ borderRadius: '3px', background: 'transparent' }}
          data-testid="referral-cancel"
        >Cancel</button>
        <button
          type="button"
          onClick={handleSave}
          disabled={!spec || (isOther(spec) && !specOther.trim())}
          className="px-3 py-1.5 text-[11px] font-bold disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ borderRadius: '3px', background: '#b48bef', color: '#0f1419', border: '1px solid #b48bef' }}
          data-testid="referral-save"
        >Save</button>
      </div>
    </div>
  );
};

const renderReferralChip = (spec) => {
  const { specialty, indication } = splitSpec(spec);
  const label = isOther(specialty) ? (otherDescOf(specialty) || 'Other') : (specialty || '?');
  // Truncate the indication so the chip stays compact.
  let detail = '';
  if (indication) {
    detail = indication.length > 38 ? `${indication.slice(0, 38)}…` : indication;
  }
  return { label, detail };
};

export const SpecialtyReferralsInput = ({
  specialties,
  addSpecialty,
  removeSpecialty,
  updateSpecialty,
  restoreSpecialty,
  quickActions,
  addQuickActionReferral,
  // Feb 28, 2026 (v6.8.43) — auto-seed explainer wiring
  autoSeedReasons,
  clearAutoSeedReason,
}) => {
  // Hide empty rows from the chip layer; they are an artifact of the
  // legacy add-then-fill pattern that's no longer user-visible.
  const items = (specialties || []).filter((s) => s && splitSpec(s).specialty);

  const handleAdd = (entry) => {
    // Find first blank to fill, else add then update.
    const blankIdx = (specialties || []).findIndex((s) => !s || !splitSpec(s).specialty);
    if (blankIdx >= 0) {
      updateSpecialty(blankIdx, entry);
    } else {
      addSpecialty();
      setTimeout(() => {
        const newIdx = (specialties || []).length;
        updateSpecialty(newIdx, entry);
      }, 0);
    }
  };

  const handleUpdate = (index, entry) => {
    const realIdx = specialties.findIndex((s) => s === items[index]);
    if (realIdx >= 0) updateSpecialty(realIdx, entry);
  };

  const handleRemove = (index) => {
    const removedItem = items[index];
    const realIdx = specialties.findIndex((s) => s === removedItem);
    if (realIdx < 0 || !removedItem) return;
    removeSpecialty(realIdx);
    // Mar 3, 2026 — Undo toast (atomic restore, no closure races).
    const { specialty, indication } = splitSpec(removedItem);
    const label = isOther(specialty) ? (otherDescOf(specialty) || 'Other') : specialty;
    toast(`Removed ${label || 'referral'}${indication ? ` · ${indication.slice(0, 40)}` : ''}`, {
      duration: 6000,
      action: {
        label: 'Undo',
        onClick: () => {
          if (typeof restoreSpecialty === 'function') {
            restoreSpecialty(removedItem);
          }
        },
      },
    });
  };

  const isStarred = (spec) => {
    const core = splitSpec(spec).specialty;
    return quickActions?.referrals?.includes(core);
  };

  // v6.8.43 — auto-seed "why?" lookups. Key format: `specialty:<core>`.
  const getChipReason = (spec) => {
    const core = splitSpec(spec).specialty;
    return (autoSeedReasons || {})[`specialty:${core}`] || null;
  };
  const handleClearChipReason = (spec) => {
    if (clearAutoSeedReason) {
      const core = splitSpec(spec).specialty;
      if (core) clearAutoSeedReason(`specialty:${core}`);
    }
  };

  return (
    <OrderChipRow
      items={items}
      renderChip={renderReferralChip}
      renderEditor={({ value, onSave, onCancel }) => (
        <ReferralEditor value={value} onSave={onSave} onCancel={onCancel} />
      )}
      addLabel="Add referral"
      accent="violet"
      sectionTitle="Specialty Referrals"
      sectionIcon={<ClipboardList className="w-3.5 h-3.5 text-violet-400" />}
      testIdPrefix="referral-chip"
      onAdd={handleAdd}
      onUpdate={handleUpdate}
      onRemove={handleRemove}
      onFavorite={(spec) => addQuickActionReferral(splitSpec(spec).specialty)}
      isStarred={isStarred}
      getChipReason={getChipReason}
      onClearChipReason={handleClearChipReason}
    />
  );
};

export default { ImagingOrdersInput, SpecialtyReferralsInput };
